import { useEffect } from 'react';
import useAddressAutocomplete from '@/hooks/useAddressAutocomplete';
import styles from './Address.module.scss';
import { InputField } from '../InputField';

const formatDisplayAddress = (details) => {
  if (details?.formattedAddress) {
    return details.formattedAddress;
  }

  const parts = [
    details?.address1,
    details?.city,
    details?.provinceCode || details?.province,
    details?.zip,
  ].filter(Boolean);

  return parts.join(', ');
};

export const Address = ({
  address,
  onChange,
  isGiftOrder,
}) => {
  const {
    inputValue,
    setInputValue,
    suggestions,
    isLoading,
    error,
    isDropdownOpen,
    handleInputChange,
    selectSuggestion,
    closeDropdown,
  } = useAddressAutocomplete();

  useEffect(() => {
    setInputValue(address || '');
  }, [address, setInputValue]);

  const handleSelect = async (suggestion) => {
    const details = await selectSuggestion(suggestion);

    if (!details) {
      return;
    }

    const formatted = formatDisplayAddress(details);
    setInputValue(formatted);

    onChange?.({
      address1: details.address1 || '',
      address2: details.address2 || '',
      city: details.city || '',
      province: details.provinceCode || details.province || '',
      provinceCode: details.provinceCode || '',
      zip: details.zip || '',
      country: details.country || 'US',
      formattedAddress: formatted,
      location: details.location,
    });
  };

  return (
    <div className={styles.addressAutocomplete}>
      <InputField
        type="search"
        value={inputValue}
        placeholder={isGiftOrder ? 'Search for Recipient Address' : 'Search for Your Address'}
        autoComplete="off"
        className="input"
        onChange={(event) => handleInputChange(event.target.value)}
        onFocus={() => {
          if (inputValue) {
            handleInputChange(inputValue);
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            closeDropdown();
          }, 150);
        }}
      />

      {isDropdownOpen && (suggestions.length > 0 || isLoading) && (
        <ul className={styles.suggestions} role="listbox">
          {isLoading ? (
            <li className={styles.suggestion}>Searching...</li>
          ) : (
            suggestions.map((suggestion) => (
              <li
                key={suggestion.placeId}
                className={styles.suggestion}
                onMouseDown={() => handleSelect(suggestion)}
                role="option"
                aria-selected="false"
              >
                <span className={styles.suggestionPrimary}>{suggestion.primaryText}</span>
                {suggestion.secondaryText ? (
                  <span className={styles.suggestionSecondary}>{suggestion.secondaryText}</span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      )}

      {isDropdownOpen && !isLoading && suggestions.length === 0 && error && (
        <div className={styles.helper}>{error}</div>
      )}

      {isDropdownOpen && !isLoading && suggestions.length === 0 && !error && (
        <div className={styles.helper}>No matching addresses found.</div>
      )}
    </div>
  );
};